/* ============================================================
   GH-1133 S5 — FreePreviewProvider (design §3.9 item 3).
   Mounted by RequireActiveBilling (App.jsx) ONLY for the 'preview' decision:
   a granted preview-arm account with no card (accessState 'new') whose
   entitlements carry the gateway's freePreview block. Every other account
   never mounts it (the context default is inactive).

   Exposes { active, readOnly, pendingVerification, mode, drafts, imagesLimit,
   trialDays, entitlements, wall, openTrialWall(source, meta), closeTrialWall,
   refresh } and renders TrialWallOverlay while a wall is open.

   Principle 2 (§2.1): the wall opens ONLY on a user action — never on a timer,
   on load, on a return visit, after a checkout return or on a background 402.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { notify } from '../../lib/notifications.js';
import { cardGateDecision } from '../../lib/pendingOAuth.js';
import { clearResumeIntent, freePreviewState, readResumeIntent } from '../../lib/freePreview.js';
import { trackPreview, trackPreviewOnce } from '../../lib/freePreviewEvents.js';
import { FreePreviewContext } from '../../contexts/FreePreviewContext.jsx';
import TrialWallOverlay from './TrialWallOverlay.jsx';

function sessionStore() {
  try { return window.sessionStorage; } catch { return null; }
}

export function FreePreviewProvider({ entitlements, onPaid, onLeave, children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [ent, setEnt] = useState(entitlements);
  const [wall, setWall] = useState(null);
  const wallRef = useRef(null);
  wallRef.current = wall;
  const state = useMemo(
    () => freePreviewState(ent) || freePreviewState(entitlements) || { mode: 'active', drafts: { ready: 0, limit: 3 }, imagesLimit: 1, trialDays: 7 },
    [ent, entitlements],
  );

  useEffect(() => { setEnt(entitlements); }, [entitlements]);

  // §3.10 preview_started: once per browser/account (diagnostic only — the
  // A/B denominator is organizations.preview_arm).
  useEffect(() => { trackPreviewOnce('preview_started', {}); }, []);

  // S13b: ?checkout=cancelled — stay in the preview, toast, strip the param.
  // The wall does NOT reopen.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') !== 'cancelled') return;
    const storage = sessionStore();
    const intent = readResumeIntent(storage);
    clearResumeIntent(storage);
    trackPreview('preview_checkout_cancelled', { source: intent?.source || null });
    notify({ level: 'info', title: t('freePreview.toastCancelled') });
    params.delete('checkout');
    params.delete('session_id');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-read entitlements (BV re-reads on focus; a card added in another tab
  // or a rollback switch lands here). Leaving the preview is RequireActiveBilling's
  // call: 'allow' once billing is real, 'gate' when the block is gone/hidden.
  const refresh = useCallback(async () => {
    try {
      api.invalidateCache?.(['/api/billing/entitlements']);
      const next = await api.billing.getEntitlements();
      const decision = cardGateDecision(next, { freePreview: true });
      if (decision !== 'preview') {
        onLeave?.(decision, next);
        return next;
      }
      setEnt(next);
      return next;
    } catch {
      return null; // keep the current board; next focus retries
    }
  }, [onLeave]);

  const openTrialWall = useCallback((source = 'cta', meta = {}) => {
    if (wallRef.current) return;
    const next = { source, meta: meta || {} };
    setWall(next);
    trackPreview('preview_wall_shown', { source, feature: meta?.feature || null, hasPost: Boolean(meta?.postId) });
  }, []);

  const closeTrialWall = useCallback((how = 'back') => {
    const current = wallRef.current;
    if (!current) return;
    setWall(null);
    trackPreview('preview_wall_dismissed', { source: current.source, how });
  }, []);

  const value = useMemo(() => ({
    active: true,
    readOnly: state.mode === 'readOnly',
    pendingVerification: state.mode === 'verify',
    mode: state.mode,
    drafts: state.drafts || { ready: 0, limit: 3 },
    imagesLimit: state.imagesLimit ?? 1,
    trialDays: state.trialDays || 7,
    entitlements: ent,
    wall,
    openTrialWall,
    closeTrialWall,
    refresh,
  }), [state.mode, state.drafts, state.imagesLimit, state.trialDays, ent, wall, openTrialWall, closeTrialWall, refresh]);

  return (
    <FreePreviewContext.Provider value={value}>
      {children}
      {wall && <TrialWallOverlay wall={wall} entitlements={ent} onClose={closeTrialWall} onPaid={onPaid} />}
    </FreePreviewContext.Provider>
  );
}

export default FreePreviewProvider;
